import { useEffect, useState } from "react";
import { Box, Button, Checkbox, HStack, Input, Select, Text, IconButton, Modal, ModalOverlay, ModalContent, ModalHeader, ModalCloseButton, ModalBody, FormControl, FormLabel, Textarea, ModalFooter, Badge, VStack, SimpleGrid, ButtonGroup, Flex } from "@chakra-ui/react";
import { CustomToast } from "../../components";
import { baseUrl, useFetch } from "../../hooks/useFetch";
import { showDate } from "../../utilities/helper";
import { cardProps } from "./billingui";
import CustomExpandTable from "../../components/CustomExpandTable";
import { Column } from "../../components/CustomExpandTable/customExpandTable";
import { CheckCircleIcon, CheckIcon, CloseIcon, DownloadIcon, EditIcon, ViewIcon } from "@chakra-ui/icons";
import { useAuth } from "../../hooks/useAuth";
import { formatMoney } from "../../utilities/money";

const NAVY = "#0F2A5C";

interface Props {
  customerId?: number;
  projectId?: number;
}

export default function InvoiceTable({ customerId, projectId }: Props) {
  const [rows, setRows] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const { addToast } = CustomToast();
  const { fetchApi } = useFetch(addToast);
  const { loginObj } = useAuth();
  const [updatingId, setUpdatingId] = useState<number | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const [payInvoice, setPayInvoice] = useState<any | null>(null);
  const [savingPayment, setSavingPayment] = useState(false);
  const emptyPay = { amount: "", paymentDate: new Date().toISOString().slice(0, 10), method: "", referenceNo: "", notes: "" };
  const [payForm, setPayForm] = useState(emptyPay);
  const [verifyingId, setVerifyingId] = useState<number | null>(null);
  const [payIntent, setPayIntent] = useState<"PartiallyPaid" | "Paid" | "Overpaid">("PartiallyPaid");

  // const [cancelTarget, setCancelTarget] = useState<any | null>(null);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelling, setCancelling] = useState(false);

  const [confirmTarget, setConfirmTarget] = useState<any | null>(null);
  const [confirmAction, setConfirmAction] = useState<"verify" | "reject" | null>(null);
  const [isSharePaypalLink, setIsSharePaypalLink] = useState(false);

  const [creditNoteTarget, setCreditNoteTarget] = useState<any | null>(null);
  const [creditNoteReason, setCreditNoteReason] = useState("");
  const [savingCreditNote, setSavingCreditNote] = useState(false);
  const [viewCreditNote, setViewCreditNote] = useState<any | null>(null);
  const [summary, setSummary] = useState<any>(null);
  const [currency, setCurrency] = useState("INR");

  const [editInvoice, setEditInvoice] = useState<any | null>(null);
  const [editItems, setEditItems] = useState<any[]>([]);
  const [savingEdit, setSavingEdit] = useState(false);

  const intentLabel: Record<string, string> = { PartiallyPaid: "Balance", Paid: "Paid", Overpaid: "Overpay" };
  const rowMoney = (r: any, v: number) => formatMoney(v ?? 0, r?.currencyCode || "INR");
  const payMoney = (v: number) => formatMoney(v, payInvoice?.currencyCode || "INR");
  const money = (amount: number, currencyCode?: string) => formatMoney(amount, currencyCode || "INR");

  //  const token = loginObj?.token;

  const methodOptions = [
    { value: 1, label: "Bank Transfer" },
    { value: 2, label: "UPI" },
    { value: 3, label: "Cheque" },
    { value: 4, label: "Cash" },
    { value: 5, label: "Card" },
    { value: 6, label: "Other" },
  ];

  const cards = [
    { label: "Due today", color: "orange", data: summary?.dueToday, due: "today" },
    { label: "Overdue", color: "red", data: summary?.overdue, due: "overdue" },
    { label: "Due in next 7 days", color: "blue", data: summary?.dueNext7, due: "next7" },
    { label: "Total outstanding", color: "purple", data: summary?.outstanding, due: null },
    { label: "Collected this month", color: "green", data: summary?.collectedThisMonth, due: null },
  ];

  const pageSize = 10;
  const statusMap: Record<string, number> = {
    Draft: 1,
    Pending: 2,
    PartiallyPaid: 3,
    Paid: 4,
    Overdue: 5,
    Cancelled: 6,
  };
  useEffect(() => {
    setLoading(true);

    const query = new URLSearchParams({
      ...(customerId != null ? { customerId: String(customerId) } : {}),
      ...(projectId != null ? { projectId: String(projectId) } : {}),
      ...(status ? { status } : {}),
      ...(search ? { search } : {}),
      page: String(page),
      pageSize: String(pageSize),
    }).toString();

    fetchApi<any>(`invoices?${query}`, "GET", null, null, "")
      .then((response) => {
        if (response) {
          setRows(response.items);
          setTotal(response.total);
        }
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId, projectId, status, search, page, pageSize, reloadKey]);

  const from = rows.length ? (page - 1) * pageSize + 1 : 0;
  const to = rows.length ? from + rows.length - 1 : 0;

  const downloadInvoicePdf = async (row: any) => {
    try {
      const response = await fetch(`${baseUrl}DownloadInvoice/${row.id}`, {
        headers: {
          Authorization: `Bearer ${loginObj?.token || ""}`,
        },
      });

      if (!response.ok) {
        addToast({ status: "error", message: "PDF download failed" });
        return;
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");

      a.href = url;
      a.download = `Invoice-${row.invoiceNumber}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();

      URL.revokeObjectURL(url);
    } catch {
      addToast({ status: "error", message: "PDF download failed" });
    }
  };

  const updateInvoiceStatus = async (row: any, newStatus: string) => {
    if (newStatus === "PartiallyPaid" || newStatus === "Paid" || newStatus === "Overpaid") {
      const payable = Math.max(0, (row.outstanding ?? 0) - (row.pendingAmount ?? 0));
      setPayIntent(newStatus);
      setPayForm({ ...emptyPay, amount: newStatus === "Paid" ? payable.toFixed(2) : "" });
      setPayInvoice(row);
      return;
    }

    try {
      setUpdatingId(row.id);
      const res = await fetchApi(`invoices/${row.id}/status`, "PUT", { Status: statusMap[newStatus] }, null, "");
      if (!res) return;
      setRows((prev) => prev.map((item) => (item.id === row.id ? { ...item, status: newStatus } : item)));
      addToast({ status: "success", message: "Invoice status updated successfully" });
    } catch {
      addToast({ status: "error", message: "Failed to update invoice status" });
    } finally {
      setUpdatingId(null);
    }
  };

  const payable = payInvoice ? Math.max(0, (payInvoice.outstanding ?? 0) - (payInvoice.pendingAmount ?? 0)) : 0;
  const amountNum = Number(payForm.amount) || 0;
  const cents = Math.round(amountNum * 100);
  const payableCents = Math.round(payable * 100);

  let amountError = "";
  if (payIntent === "PartiallyPaid") {
    if (payableCents <= 0) amountError = "Nothing balance left on this invoice";
    else if (cents > 0 && cents >= payableCents) amountError = `For Balance, amount must be less than ${payMoney(payable)}`;
  } else if (payIntent === "Overpaid") {
    if (cents > 0 && cents <= payableCents) amountError = `For Overpay, amount must be greater than ${payMoney(payable)}`;
  } else if (payIntent === "Paid") {
    if (payableCents <= 0) amountError = "Nothing payable on this invoice";
  }

  const amountValid = payIntent === "Paid" ? payableCents > 0 : cents > 0 && !amountError;
  const canSavePayment = amountValid && !!payForm.method && !!payForm.paymentDate;

  const savePayment = async () => {
    if (!payInvoice || !canSavePayment) return;
    try {
      setSavingPayment(true);
      const res = await fetchApi(
        `invoices/${payInvoice.id}/payments`,
        "POST",
        {
          PaymentDate: payForm.paymentDate,
          Amount: amountNum,
          Method: Number(payForm.method),
          ReferenceNo: payForm.referenceNo || null,
          Notes: payForm.notes || null,
        },
        null,
        "",
      );
      if (!res) return;
      addToast({ status: "success", message: "Payment recorded. Waiting for verification" });
      setPayInvoice(null);
      setReloadKey((k) => k + 1);
    } finally {
      setSavingPayment(false);
    }
  };

  const verifyInvoice = async (row: any, sharePaypal = false): Promise<boolean> => {
    try {
      setVerifyingId(row.id);
      const res = await fetchApi(`invoices/${row.id}/verify`, "POST", { isSharePaypalLink: sharePaypal }, null, "");
      if (!res) return false;
      // Approved: status stays Pending, but the invoice is now verified
      setRows((prev) => prev.map((i) => (i.id === row.id ? { ...i, isVerified: true, status: "Pending" } : i)));
      addToast({ status: "success", message: "Invoice verified" });
      return true;
    } finally {
      setVerifyingId(null);
    }
  };

  const cancelInvoice = async (row: any, reason: string): Promise<boolean> => {
    try {
      setCancelling(true);
      const res = await fetchApi(`invoices/${row.id}/cancel`, "POST", { Reason: reason }, null, "");
      if (!res) return false;
      setRows((prev) => prev.map((i) => (i.id === row.id ? { ...i, status: "Cancelled", cancelReason: reason } : i)));
      addToast({ status: "success", message: "Invoice rejected and cancelled" });
      return true;
    } finally {
      setCancelling(false);
    }
  };

  const closeConfirm = () => {
    setConfirmTarget(null);
    setConfirmAction(null);
    setCancelReason("");
    setIsSharePaypalLink(false);
  };

  const confirmBusy = verifyingId !== null || cancelling;

  const confirmVerificationAction = async () => {
    if (!confirmTarget || !confirmAction || confirmBusy) return;

    if (confirmAction === "verify") {
      if (await verifyInvoice(confirmTarget, isSharePaypalLink)) closeConfirm();
      return;
    }

    const reason = cancelReason.trim();
    if (!reason) return;
    if (await cancelInvoice(confirmTarget, reason)) closeConfirm();
  };

  const isPaidRow = (r: any) => r.status === "Paid" || r.status === "Overpaid";
  const isCancelled = (r: any) => r.status === "Cancelled";
  const rowClass = (r: any) => (isPaidRow(r) ? "row-green" : isCancelled(r) ? "row-red" : "");

  const saveCreditNote = async () => {
    if (!creditNoteTarget || !creditNoteReason.trim()) return;

    try {
      setSavingCreditNote(true);

      const res = await fetchApi(`invoices/${creditNoteTarget.id}/credit-note`, "POST", { Reason: creditNoteReason.trim() }, null, "");

      if (!res) return;

      addToast({
        status: "success",
        message: "Credit note saved successfully",
      });

      setCreditNoteTarget(null);
      setCreditNoteReason("");
      setReloadKey((k) => k + 1);
    } finally {
      setSavingCreditNote(false);
    }
  };

  const openEditInvoice = async (row: any) => {
    const res = await fetchApi<any>(`invoices/${row.id}`, "GET", null, null, "");

    if (!res) return;

    setEditInvoice(res);
    setEditItems(
      res.items.map((item: any) => ({
        description: item.description,
        quantity: String(item.quantity),
        rate: String(item.rate),
      })),
    );
  };

  const saveInvoiceEdit = async () => {
    if (!editInvoice || !editItems.length) return;

    try {
      setSavingEdit(true);

      const res = await fetchApi(
        `invoices/${editInvoice.id}/edit`,
        "PUT",
        {
          Items: editItems.map((item) => ({
            Description: item.description,
            Quantity: Number(item.quantity),
            Rate: Number(item.rate),
          })),
        },
        null,
        "",
      );

      if (!res) return;

      addToast({ status: "success", message: "Invoice updated successfully" });

      setEditInvoice(null);
      setReloadKey((k) => k + 1);
    } finally {
      setSavingEdit(false);
    }
  };

  useEffect(() => {
    fetchApi<any>(`invoices/summary?customerId=${customerId}&currency=${currency}`, "GET", null, null, "").then((r) => r && setSummary(r));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currency]);


  return (
    <Box
      {...cardProps}
      p={5}
      sx={{
        "& tr:has(.row-green) > td": { backgroundColor: "#DCFCE7 !important" },
        "& tr:has(.row-red) > td": { backgroundColor: "#FEE2E2 !important" },
      }}
    >
      <Flex justify="flex-end" mb={3}>
        <ButtonGroup size="sm" isAttached variant="outline">
          <Button colorScheme={currency === "INR" ? "blue" : undefined} variant={currency === "INR" ? "solid" : "outline"} onClick={() => setCurrency("INR")}>
            ₹ INR
          </Button>
          <Button colorScheme={currency === "USD" ? "blue" : undefined} variant={currency === "USD" ? "solid" : "outline"} onClick={() => setCurrency("USD")}>
            $ USD
          </Button>
        </ButtonGroup>
      </Flex>

      <SimpleGrid columns={{ base: 1, md: 2, xl: 5 }} spacing={4} mb={5}>
        {cards.map((c) => {
          const active = c.due !== null
          //filters.due === c.due;
          return (
            <Box key={c.label} {...cardProps} p={4} cursor={c.due !== null ? "pointer" : "default"} borderWidth="1px" borderColor={active ? `${c.color}.400` : "transparent"} bg={active ? `${c.color}.50` : undefined} _hover={c.due !== null ? { borderColor: `${c.color}.300` } : undefined}
              onClick={() => c.due !== null
                //updateFilter({ due: active ? "" : c.due! })
              }>
              <Text fontSize="xs" fontWeight="600" color="gray.500" textTransform="uppercase" letterSpacing="0.4px">
                {c.label}
              </Text>
              <Text fontSize="2xl" fontWeight="700" color={`${c.color}.600`} mt={1}>
                {money(c.data?.amount ?? 0, currency)}
              </Text>
              {c.data?.count != null && (
                <Text fontSize="xs" color="gray.500">
                  {c.data.count} invoice{c.data.count === 1 ? "" : "s"}
                </Text>
              )}
            </Box>
          );
        })}
      </SimpleGrid>

      <HStack mb={4} spacing={3}>
        <Select
          size="sm"
          borderRadius="md"
          maxW="180px"
          placeholder="All Status"
          value={status}
          onChange={(e) => {
            setPage(1);
            setStatus(e.target.value);
          }}
        >
          {["Pending", "PartiallyPaid", "Paid", "Overdue", "Cancelled"].map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </Select>
        <Input
          size="sm"
          borderRadius="md"
          maxW="260px"
          placeholder="Search invoice no..."
          value={search}
          onChange={(e) => {
            setPage(1);
            setSearch(e.target.value);
          }}
        />
      </HStack>

      <CustomExpandTable value={rows} loading={loading} rowKey="id" headerBg="#FFFFFF" headerTextColor="#52627A" emptyMessage="No invoices" striped={false} renderExpandedContent={() => null}>
        <Column header="S.NO" body={(_, index) => index + 1} />
        <Column
          header="Invoice No."
          field="invoiceNumber"
          body={(r: any) => (
            <Text className={rowClass(r)} fontWeight="600" fontSize="13px">
              {r.invoiceNumber}
            </Text>
          )}
        />

        <Column header="Customer" field="customer" body={(r: any) => <Text fontSize="13px">{r.customer || "-"}</Text>} />

        <Column
          header="Project"
          field="project"
          body={(r: any) => (
            <Text fontSize="13px" color="gray.600">
              {r.project || "-"}
            </Text>
          )}
        />

        <Column
          header="Amount"
          field="totalAmount"
          body={(r: any) => (
            <Text fontSize="13px" whiteSpace="nowrap">
              {rowMoney(r, r.totalAmount)}
            </Text>
          )}
        />
        <Column
          header="Paid"
          field="paidAmount"
          body={(r: any) => (
            <Text fontSize="13px" whiteSpace="nowrap">
              {rowMoney(r, r.paidAmount)}
            </Text>
          )}
        />

        <Column
          header="Outstanding"
          field="outstanding"
          body={(r: any) => (
            <Text fontSize="13px" whiteSpace="nowrap">
              {rowMoney(r, r.outstanding)}
            </Text>
          )}
        />

        <Column
          header="Due Date"
          field="dueDate"
          body={(r: any) => (
            <Text fontSize="13px" color="gray.600" whiteSpace="nowrap">
              {showDate(r.dueDate)}
            </Text>
          )}
        />

        <Column
          header="Status"
          field="status"
          body={(r: any) => {
            if (r.status === "Paid") {
              return (
                <Badge colorScheme="green" variant="subtle" px={3} py={1.5} borderRadius="full" fontSize="xs" fontWeight="600">
                  <HStack spacing={1}>
                    <CheckCircleIcon boxSize={3} />
                    <Text>Paid</Text>
                  </HStack>
                </Badge>
              );
            }

            if (r.status === "Cancelled") {
              return (
                <Badge colorScheme="red" variant="subtle" px={3} py={1.5} borderRadius="full">
                  Cancelled
                </Badge>
              );
            }

            return (
              <Select size="sm" width="150px" value={r.status} isDisabled={updatingId === r.id || !r.isVerified} title={!r.isVerified ? "Verify the invoice first" : undefined} onChange={(e) => updateInvoiceStatus(r, e.target.value)} borderRadius="md" variant="filled">
                {!["Pending", "PartiallyPaid", "Paid", "Overpaid"].includes(r.status) && (
                  <option value={r.status} disabled>
                    {r.status}
                  </option>
                )}
                <option value="Pending">Pending</option>
                <option value="PartiallyPaid">Balance</option>
                <option value="Paid">Paid</option>
                <option value="Overpaid">Overpay</option>
              </Select>
            );
          }}
        />

        <Column
          header="Credit Note"
          body={(r: any) => (
            <>
              {r.creditNoteReason ? (
                <IconButton aria-label="View credit note" icon={<ViewIcon />} size="xs" variant="ghost" colorScheme="blue" onClick={() => setViewCreditNote(r)} />
              ) : r.status === "PartiallyPaid" ? (
                <Button
                  size="xs"
                  colorScheme="blue"
                  variant="outline"
                  onClick={() => {
                    setCreditNoteReason("");
                    setCreditNoteTarget(r);
                  }}
                >
                  Credit Note
                </Button>
              ) : (
                <Text fontSize="12px" color="gray.400">
                  -
                </Text>
              )}
            </>
          )}
        />
        <Column
          header="Verification"
          body={(r: any) =>
            isCancelled(r) ? (
              <Box maxW="200px">
                <Text fontSize="12px" fontWeight="600" color="red.600">
                  Cancelled
                </Text>
                <Text fontSize="11px" color="red.500" noOfLines={2} title={r.cancelReason || ""}>
                  {r.cancelReason}
                </Text>
              </Box>
            ) : r.isVerified ? (
              <Text fontSize="12px" fontWeight="600" color="green.600">
                Verified
              </Text>
            ) : r.status === "Pending" ? (
              <HStack spacing={2}>
                <IconButton
                  aria-label="Verify invoice"
                  icon={<CheckIcon />}
                  size="xs"
                  colorScheme="green"
                  variant="outline"
                  isLoading={verifyingId === r.id}
                  onClick={() => {
                    setConfirmTarget(r);
                    setConfirmAction("verify");
                    setIsSharePaypalLink(false);
                  }}
                />
                <IconButton
                  aria-label="Reject invoice"
                  icon={<CloseIcon />}
                  size="xs"
                  colorScheme="red"
                  variant="outline"
                  onClick={() => {
                    setConfirmTarget(r);
                    setConfirmAction("reject");
                  }}
                />
              </HStack>
            ) : (
              <Text fontSize="12px" color="gray.400">
                -
              </Text>
            )
          }
        />
        <Column
          header="Edit"
          body={(r: any) =>
            !r.isVerified && r.paidAmount === 0 && r.status !== "Cancelled" ? (
              <IconButton aria-label="Edit invoice" icon={<EditIcon />} size="xs" colorScheme="blue" variant="ghost" onClick={() => openEditInvoice(r)} />
            ) : (
              <Text fontSize="12px" color="gray.400">
                Locked
              </Text>
            )
          }
        />
        <Column header="Invoice PDF" body={(r: any) => <IconButton aria-label="Download invoice PDF" icon={<DownloadIcon />} size="xs" variant="ghost" onClick={() => downloadInvoicePdf(r)} />} />
      </CustomExpandTable>

      <HStack justify="space-between" mt={4}>
        <Text fontSize="xs" color="gray.500">
          Showing {from}–{to} of {total}
        </Text>
        <HStack spacing={2}>
          <Button size="xs" variant="outline" onClick={() => setPage((p) => p - 1)} isDisabled={page === 1}>
            Prev
          </Button>
          <Button size="xs" colorScheme="blue" cursor="default" pointerEvents="none">
            {page}
          </Button>
          <Button size="xs" variant="outline" onClick={() => setPage((p) => p + 1)} isDisabled={page * pageSize >= total}>
            Next
          </Button>
        </HStack>
      </HStack>

      {/* PAYMENT MODAL */}
      <Modal isOpen={!!payInvoice} onClose={() => setPayInvoice(null)} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {intentLabel[payIntent]} payment · {payInvoice?.invoiceNumber}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack align="stretch" spacing={3}>
              <Box bg="gray.50" borderRadius="md" p={3} fontSize="13px">
                <HStack justify="space-between">
                  <Text>Invoice total</Text>
                  <Text fontWeight="600">{payMoney(payInvoice?.totalAmount ?? 0)}</Text>
                </HStack>
                <HStack justify="space-between">
                  <Text>Paid (verified)</Text>
                  <Text>{payMoney(payInvoice?.paidAmount ?? 0)}</Text>
                </HStack>
                {(payInvoice?.pendingAmount ?? 0) > 0 && (
                  <HStack justify="space-between">
                    <Text>Awaiting verification</Text>
                    <Text color="orange.500">{payMoney(payInvoice?.pendingAmount ?? 0)}</Text>
                  </HStack>
                )}
                <HStack justify="space-between">
                  <Text fontWeight="600">Payable now</Text>
                  <Text fontWeight="700">{payMoney(payable)}</Text>
                </HStack>
              </Box>

              <FormControl isRequired>
                <FormLabel fontSize="sm">Amount</FormLabel>
                <Input type="number" min={0} value={payForm.amount} isDisabled={payIntent === "Paid"} isInvalid={!!amountError} onChange={(e) => setPayForm((f) => ({ ...f, amount: e.target.value }))} />

                {amountError && (
                  <Text mt={2} fontSize="xs" color="red.500">
                    {amountError}
                  </Text>
                )}
                {!amountError && payIntent === "PartiallyPaid" && cents > 0 && (
                  <Badge mt={2} colorScheme="orange">
                    Balance remaining · {payMoney(payable - amountNum)}
                  </Badge>
                )}
                {!amountError && payIntent === "Paid" && payableCents > 0 && (
                  <Badge mt={2} colorScheme="green">
                    Paid in full
                  </Badge>
                )}
                {!amountError && payIntent === "Overpaid" && cents > 0 && (
                  <Badge mt={2} colorScheme="purple">
                    Overpaid by · {payMoney(amountNum - payable)}
                  </Badge>
                )}
              </FormControl>

              <HStack align="flex-start">
                <FormControl isRequired>
                  <FormLabel fontSize="sm">Payment date</FormLabel>
                  <Input type="date" max={new Date().toISOString().slice(0, 10)} value={payForm.paymentDate} onChange={(e) => setPayForm((f) => ({ ...f, paymentDate: e.target.value }))} />
                </FormControl>
                <FormControl isRequired>
                  <FormLabel fontSize="sm">Payment mode</FormLabel>
                  <Select placeholder="Select" value={payForm.method} onChange={(e) => setPayForm((f) => ({ ...f, method: e.target.value }))}>
                    {methodOptions.map((m) => (
                      <option key={m.value} value={m.value}>
                        {m.label}
                      </option>
                    ))}
                  </Select>
                </FormControl>
              </HStack>

              <FormControl>
                <FormLabel fontSize="sm">Reference no.</FormLabel>
                <Input placeholder="UTR / cheque no / txn id" value={payForm.referenceNo} onChange={(e) => setPayForm((f) => ({ ...f, referenceNo: e.target.value }))} />
              </FormControl>

              <FormControl>
                <FormLabel fontSize="sm">Note</FormLabel>
                <Textarea rows={2} value={payForm.notes} onChange={(e) => setPayForm((f) => ({ ...f, notes: e.target.value }))} />
              </FormControl>

              <Text fontSize="xs" color="gray.500">
                The invoice status updates only after this payment is verified.
              </Text>
            </VStack>
          </ModalBody>
          <ModalFooter gap={2}>
            <Button variant="ghost" onClick={() => setPayInvoice(null)}>
              Cancel
            </Button>
            <Button colorScheme="blue" onClick={savePayment} isDisabled={!canSavePayment} isLoading={savingPayment}>
              Save payment
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* VERIFY / REJECT CONFIRM MODAL */}
      <Modal isOpen={!!confirmTarget} onClose={closeConfirm} isCentered closeOnOverlayClick={!confirmBusy}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {confirmAction === "verify" ? "Verify Invoice" : "Reject Invoice"} · {confirmTarget?.invoiceNumber}
          </ModalHeader>
          <ModalCloseButton isDisabled={confirmBusy} />
          <ModalBody>
            {confirmAction === "verify" ? (
              <>
                <Text fontSize="sm" color="gray.700" mb={4}>
                  Are you sure you want to verify invoice <strong>{confirmTarget?.invoiceNumber}</strong>? After verification, the status can be updated and payments can be recorded.
                </Text>
                <Box p={3.5} bg="blue.50" borderRadius="md" border="1px solid" borderColor="blue.100">
                  <Checkbox colorScheme="blue" isChecked={isSharePaypalLink} onChange={(e) => setIsSharePaypalLink(e.target.checked)}>
                    <Text fontSize="sm" fontWeight="medium" color={NAVY}>
                      Share PayPal link to send to email
                    </Text>
                  </Checkbox>
                  <Text fontSize="xs" color="gray.600" mt={1.5} pl={6}>
                    If checked, PayPal payment link will be sent to the client email.
                  </Text>
                </Box>
              </>
            ) : (
              <FormControl isRequired>
                <FormLabel fontSize="sm">Reason for rejection</FormLabel>
                <Textarea rows={3} value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} placeholder="e.g. Duplicate invoice / wrong amount" autoFocus />
                <Text mt={2} fontSize="xs" color="red.500">
                  Rejecting will cancel this invoice.
                </Text>
              </FormControl>
            )}
          </ModalBody>
          <ModalFooter gap={2}>
            <Button variant="ghost" onClick={closeConfirm} isDisabled={confirmBusy}>
              No, go back
            </Button>
            <Button colorScheme={confirmAction === "verify" ? "green" : "red"} onClick={confirmVerificationAction} isDisabled={confirmAction === "reject" && !cancelReason.trim()} isLoading={confirmBusy}>
              {confirmAction === "verify" ? "Yes, verify" : "Reject invoice"}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* CREDIT NOTE MODAL */}
      <Modal isOpen={!!creditNoteTarget} onClose={() => setCreditNoteTarget(null)} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Credit Note · {creditNoteTarget?.invoiceNumber}</ModalHeader>

          <ModalCloseButton />

          <ModalBody>
            <FormControl isRequired>
              <FormLabel fontSize="sm">Credit Note Reason</FormLabel>
              <Textarea rows={4} placeholder="Write the reason for issuing this credit note..." value={creditNoteReason} onChange={(e) => setCreditNoteReason(e.target.value)} />
            </FormControl>
          </ModalBody>

          <ModalFooter gap={2}>
            <Button variant="ghost" onClick={() => setCreditNoteTarget(null)}>
              Cancel
            </Button>

            <Button colorScheme="blue" onClick={saveCreditNote} isDisabled={!creditNoteReason.trim()} isLoading={savingCreditNote}>
              Save Credit Note
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      {/* VIEW CREDIT NOTE MODAL */}
      <Modal isOpen={!!viewCreditNote} onClose={() => setViewCreditNote(null)} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Credit Note · {viewCreditNote?.invoiceNumber}</ModalHeader>

          <ModalCloseButton />

          <ModalBody>
            <FormControl>
              <FormLabel fontSize="sm">Credit Note Reason</FormLabel>
              <Textarea rows={4} value={viewCreditNote?.creditNoteReason || ""} isReadOnly />
            </FormControl>
          </ModalBody>

          <ModalFooter>
            <Button onClick={() => setViewCreditNote(null)}>Close</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      {/* EDIT INVOICE MODAL */}
      <Modal isOpen={!!editInvoice} onClose={() => setEditInvoice(null)} size="xl" isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Edit Invoice · {editInvoice?.invoiceNumber}</ModalHeader>

          <ModalCloseButton />

          <ModalBody>
            <VStack align="stretch" spacing={3}>
              {editItems.map((item, index) => (
                <Box key={index} p={3} borderWidth="1px" borderRadius="md">
                  <FormControl mb={2}>
                    <FormLabel fontSize="sm">Description</FormLabel>
                    <Input value={item.description} onChange={(e) => setEditItems((prev) => prev.map((x, i) => (i === index ? { ...x, description: e.target.value } : x)))} />
                  </FormControl>

                  <HStack align="flex-end">
                    <FormControl>
                      <FormLabel fontSize="sm">Quantity</FormLabel>
                      <Input type="number" min="0.01" value={item.quantity} onChange={(e) => setEditItems((prev) => prev.map((x, i) => (i === index ? { ...x, quantity: e.target.value } : x)))} />
                    </FormControl>

                    <FormControl>
                      <FormLabel fontSize="sm">Rate</FormLabel>
                      <Input type="number" min="0" value={item.rate} onChange={(e) => setEditItems((prev) => prev.map((x, i) => (i === index ? { ...x, rate: e.target.value } : x)))} />
                    </FormControl>

                    <Button size="sm" colorScheme="red" variant="ghost" onClick={() => setEditItems((prev) => prev.filter((_, i) => i !== index))} isDisabled={editItems.length === 1}>
                      Remove
                    </Button>
                  </HStack>

                  <Text fontSize="sm" fontWeight="600" mt={2}>
                    Amount: {rowMoney(editInvoice, Number(item.quantity || 0) * Number(item.rate || 0))}
                  </Text>
                </Box>
              ))}

              <Button size="sm" variant="outline" onClick={() => setEditItems((prev) => [...prev, { description: "", quantity: "1", rate: "0" }])}>
                + Add item
              </Button>

              <Box bg="gray.50" p={3} borderRadius="md">
                <HStack justify="space-between">
                  <Text fontSize="sm">Subtotal</Text>
                  <Text fontWeight="600">
                    {rowMoney(
                      editInvoice,
                      editItems.reduce((sum, x) => sum + Number(x.quantity || 0) * Number(x.rate || 0), 0),
                    )}
                  </Text>
                </HStack>

                <HStack justify="space-between" mt={2}>
                  <Text fontSize="sm">GST ({editInvoice?.gstPercent || 0}%)</Text>
                  <Text fontWeight="600">{rowMoney(editInvoice, Math.round(editItems.reduce((sum, x) => sum + Number(x.quantity || 0) * Number(x.rate || 0), 0) * (editInvoice?.gstPercent || 0) * 100) / 10000)}</Text>
                </HStack>

                <HStack justify="space-between" mt={2}>
                  <Text fontWeight="700">Total</Text>
                  <Text fontWeight="700" color="blue.600">
                    {rowMoney(
                      editInvoice,
                      (() => {
                        const subtotal = editItems.reduce((sum, x) => sum + Number(x.quantity || 0) * Number(x.rate || 0), 0);
                        const gst = Math.round(subtotal * (editInvoice?.gstPercent || 0)) / 100;

                        return subtotal + gst;
                      })(),
                    )}
                  </Text>
                </HStack>
              </Box>
            </VStack>
          </ModalBody>

          <ModalFooter gap={2}>
            <Button variant="ghost" onClick={() => setEditInvoice(null)}>
              Cancel
            </Button>

            <Button colorScheme="blue" onClick={saveInvoiceEdit} isLoading={savingEdit} isDisabled={!editItems.length || editItems.some((x) => !x.description.trim() || Number(x.quantity) <= 0 || Number(x.rate) < 0)}>
              Save Changes
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}
